import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { format, parseISO, isPast, isFuture, differenceInDays } from 'date-fns';
import { it } from 'date-fns/locale';
import {
  Package,
  Clock,
  Users,
  ShoppingCart,
  Calendar,
  Truck,
  ChevronRight,
  Plus,
  Minus,
  Check,
  AlertCircle,
  Building2,
  Euro,
  FileText,
  RefreshCw,
  Edit,
  Send,
  Eye,
  BarChart3,
  Phone,
  Trash2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { Separator } from '../components/ui/separator';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { getErrorMessage } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

interface GroupageProduct {
  product_id: string;
  product_name: string;
  name?: string;
  product_sku?: string;
  unit: string;
  unit_price: number;
  min_quantity: number;
  max_quantity?: number;
  max_quantity_per_buyer?: number;  // Limite per buyer
  max_quantity_total?: number;  // Stock totale
  current_quantity_sold?: number;  // Venduto finora
  available_quantity?: number;
  notes?: string;
}

interface GroupageOpportunity {
  id: string;
  association_id: string;
  title: string;
  description?: string;
  seller_company_id: string;
  seller_company_name: string;
  products: GroupageProduct[];
  collection_start: string;
  collection_end: string;
  collection_type?: 'days' | 'date';  // Tipo chiusura
  collection_days?: number;  // Giorni se type=days
  expected_delivery_start?: string;
  expected_delivery_end?: string;
  min_participants: number;
  min_total_quantity?: number;
  min_total_value?: number;
  shipping_notes?: string;
  shipping_cost_type: string;
  shipping_cost: number;
  payment_notes?: string;
  payment_methods: string[];
  status: string;
  participants_count: number;
  total_orders: number;
  total_quantity: number;
  total_value: number;
  stock_enabled?: boolean;  // Opzioni stock abilitate
  deleted_at?: string;  // Data eliminazione (soft delete)
  my_order?: GroupageOrder;
}

interface GroupageOrderItem {
  product_id: string;
  product_name: string;
  name?: string;
  quantity: number;
  unit_price: number;
  total_price: number;
  notes?: string;
}

interface GroupageOrder {
  id: string;
  groupage_id: string;
  items: GroupageOrderItem[];
  subtotal: number;
  shipping_cost: number;
  total: number;
  status: string;
  delivery_address?: string;
  delivery_city?: string;
  delivery_postal_code?: string;
  delivery_province?: string;
  delivery_notes?: string;
  member_notes?: string;
}

const GroupagePage: React.FC = () => {
  const { user } = useAuth();
  const [opportunities, setOpportunities] = useState<GroupageOpportunity[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('active');
  
  // Order dialog
  const [showOrderDialog, setShowOrderDialog] = useState(false);
  const [selectedOpportunity, setSelectedOpportunity] = useState<GroupageOpportunity | null>(null);
  const [orderItems, setOrderItems] = useState<Record<string, number>>({});
  const [orderNotes, setOrderNotes] = useState('');
  const [deliveryNotes, setDeliveryNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Admin detail dialog
  const [showAdminDetailDialog, setShowAdminDetailDialog] = useState(false);
  const [adminDetailGroupage, setAdminDetailGroupage] = useState<any>(null);
  const [groupageOrders, setGroupageOrders] = useState<any[]>([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [selectedBuyerOrder, setSelectedBuyerOrder] = useState<any>(null);
  
  // Delete dialog
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [groupageToDelete, setGroupageToDelete] = useState<GroupageOpportunity | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (user?.association_id) {
      fetchOpportunities();
    }
  }, [user?.association_id]);

  const fetchOpportunities = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/groupage/opportunities`);
      setOpportunities(res.data.opportunities || []);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string, collectionEnd?: string) => {
    if (!collectionEnd) {
      if (status === 'completed') return <Badge className="bg-blue-500 text-white">Completato</Badge>;
      if (status === 'closed') return <Badge variant="secondary">Chiuso</Badge>;
      return <Badge variant="outline">{status}</Badge>;
    }
    const endDate = parseISO(collectionEnd);
    const daysLeft = differenceInDays(endDate, new Date());
    
    switch (status) {
      case 'published':
      case 'collecting':
        if (daysLeft <= 1) {
          return <Badge className="bg-red-500 text-white">Chiude oggi!</Badge>;
        } else if (daysLeft <= 3) {
          return <Badge className="bg-orange-500 text-white">Chiude tra {daysLeft} giorni</Badge>;
        }
        return <Badge className="bg-green-500 text-white">Aperto</Badge>;
      case 'closed':
        return <Badge variant="secondary">Chiuso</Badge>;
      case 'completed':
        return <Badge className="bg-blue-500 text-white">Completato</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const canOrder = (opportunity: GroupageOpportunity): boolean => {
    if (!['published', 'collecting'].includes(opportunity.status)) return false;
    if (!opportunity.collection_end) return true;
    const endDate = parseISO(opportunity.collection_end);
    return !isPast(endDate);
  };

  const openOrderDialog = (opportunity: GroupageOpportunity) => {
    setSelectedOpportunity(opportunity);
    
    // Se ha già un ordine, pre-popola i dati
    if (opportunity.my_order) {
      const items: Record<string, number> = {};
      opportunity.my_order.items.forEach(item => {
        items[item.product_id] = item.quantity;
      });
      setOrderItems(items);
      setOrderNotes(opportunity.my_order.member_notes || '');
      setDeliveryNotes(opportunity.my_order.delivery_notes || '');
    } else {
      setOrderItems({});
      setOrderNotes('');
      setDeliveryNotes('');
    }
    
    setShowOrderDialog(true);
  };

  const updateQuantity = (productId: string, delta: number, minQty: number, maxQty?: number) => {
    setOrderItems(prev => {
      const current = prev[productId] || 0;
      let newQty = current + delta;
      
      if (newQty < 0) newQty = 0;
      if (newQty > 0 && newQty < minQty) newQty = delta > 0 ? minQty : 0;
      if (maxQty && newQty > maxQty) newQty = maxQty;
      
      if (newQty === 0) {
        const { [productId]: _, ...rest } = prev;
        return rest;
      }
      
      return { ...prev, [productId]: newQty };
    });
  };

  const setQuantity = (productId: string, value: string, minQty: number, maxQty?: number) => {
    const qty = parseInt(value) || 0;
    let finalQty = qty;
    
    if (qty > 0 && qty < minQty) finalQty = minQty;
    if (maxQty && qty > maxQty) finalQty = maxQty;
    
    setOrderItems(prev => {
      if (finalQty === 0) {
        const { [productId]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [productId]: finalQty };
    });
  };

  const calculateOrderTotal = (): { subtotal: number; shipping: number; total: number } => {
    if (!selectedOpportunity) return { subtotal: 0, shipping: 0, total: 0 };
    
    let subtotal = 0;
    selectedOpportunity.products.forEach(product => {
      const qty = orderItems[product.product_id] || 0;
      subtotal += qty * product.unit_price;
    });
    
    const shipping = selectedOpportunity.shipping_cost_type === 'included' ? 0 : (selectedOpportunity.shipping_cost || 0);
    
    return {
      subtotal,
      shipping,
      total: subtotal + (shipping || 0)
    };
  };

  const handleSubmitOrder = async () => {
    if (!selectedOpportunity) return;
    
    const itemsToSubmit = selectedOpportunity.products
      .filter(p => orderItems[p.product_id] > 0)
      .map(p => ({
        product_id: p.product_id,
        product_name: p.product_name || p.name,
        quantity: orderItems[p.product_id],
        unit_price: p.unit_price,
        total_price: orderItems[p.product_id] * p.unit_price
      }));
    
    if (itemsToSubmit.length === 0) {
      toast.error('Seleziona almeno un prodotto');
      return;
    }
    
    setSubmitting(true);
    
    try {
      if (selectedOpportunity.my_order) {
        // Aggiorna ordine esistente
        await axios.put(`${API}/groupage/orders/${selectedOpportunity.my_order.id}`, {
          items: itemsToSubmit,
          member_notes: orderNotes,
          delivery_notes: deliveryNotes
        });
        toast.success('Ordine aggiornato');
      } else {
        // Crea nuovo ordine
        await axios.post(`${API}/groupage/opportunities/${selectedOpportunity.id}/orders`, {
          groupage_id: selectedOpportunity.id,
          items: itemsToSubmit,
          member_notes: orderNotes,
          delivery_notes: deliveryNotes
        });
        toast.success('Ordine inserito');
      }
      
      setShowOrderDialog(false);
      fetchOpportunities();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmOrder = async (orderId: string) => {
    try {
      await axios.post(`${API}/groupage/orders/${orderId}/confirm`);
      toast.success('Ordine confermato');
      fetchOpportunities();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleCancelOrder = async (orderId: string) => {
    try {
      await axios.delete(`${API}/groupage/orders/${orderId}`);
      toast.success('Ordine annullato');
      fetchOpportunities();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  // Filtra opportunità per tab
  const filteredOpportunities = opportunities.filter(opp => {
    if (activeTab === 'active') {
      return ['published', 'collecting'].includes(opp.status) && !opp.deleted_at;
    } else if (activeTab === 'my-orders') {
      return opp.my_order !== undefined && !opp.deleted_at;
    } else if (activeTab === 'drafts') {
      return ['draft', 'pending_approval'].includes(opp.status) && !opp.deleted_at;
    } else if (activeTab === 'deleted') {
      return opp.deleted_at !== undefined;
    } else {
      return ['closed', 'completed'].includes(opp.status) && !opp.deleted_at;
    }
  });

  // Conteggi per badge tabs
  const activeCount = opportunities.filter(o => ['published', 'collecting'].includes(o.status) && !o.deleted_at).length;
  const myOrdersCount = opportunities.filter(o => o.my_order && !o.deleted_at).length;
  const draftsCount = opportunities.filter(o => ['draft', 'pending_approval'].includes(o.status) && !o.deleted_at).length;
  const deletedCount = opportunities.filter(o => o.deleted_at).length;

  // Chiudi groupage anticipatamente
  const handleCloseGroupage = async (groupageId: string) => {
    try {
      await axios.post(`${API}/groupage/opportunities/${groupageId}/close`);
      toast.success('Raccolta ordini chiusa!');
      fetchOpportunities();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  // Completa groupage e invia al fornitore
  const handleCompleteGroupage = async (groupageId: string) => {
    try {
      await axios.post(`${API}/groupage/opportunities/${groupageId}/complete`);
      toast.success('Groupage completato! Ordini inviati al fornitore.');
      fetchOpportunities();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  // Apri pannello dettaglio admin
  const openAdminDetail = async (opportunity: GroupageOpportunity) => {
    setAdminDetailGroupage(opportunity);
    setShowAdminDetailDialog(true);
    setLoadingOrders(true);
    
    try {
      const res = await axios.get(`${API}/groupage/opportunities/${opportunity.id}/orders`);
      setGroupageOrders(res.data.orders || []);
    } catch (error) {
      toast.error('Errore nel caricamento degli ordini');
      setGroupageOrders([]);
    } finally {
      setLoadingOrders(false);
    }
  };

  // Pubblica groupage
  const handlePublishGroupage = async (groupageId: string) => {
    try {
      await axios.post(`${API}/groupage/opportunities/${groupageId}/publish`);
      toast.success('Groupage pubblicato!');
      fetchOpportunities();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  // Conferma eliminazione groupage
  const openDeleteDialog = (opportunity: GroupageOpportunity) => {
    setGroupageToDelete(opportunity);
    setShowDeleteDialog(true);
  };

  // Elimina groupage
  const handleDeleteGroupage = async () => {
    if (!groupageToDelete) return;
    
    setDeleting(true);
    try {
      await axios.delete(`${API}/groupage/opportunities/${groupageToDelete.id}`);
      toast.success('Groupage eliminato con successo');
      setShowDeleteDialog(false);
      setGroupageToDelete(null);
      fetchOpportunities();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setDeleting(false);
    }
  };

  if (!user?.association_id) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-center" data-testid="no-association-message">
        <Building2 className="h-16 w-16 text-muted-foreground mb-4" />
        <h2 className="text-xl font-semibold mb-2">Nessuna Associazione</h2>
        <p className="text-muted-foreground max-w-md">
          Per accedere al Groupage devi far parte di un'associazione.
          Contatta il tuo referente per essere aggiunto.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="groupage-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Groupage</h1>
          <p className="text-muted-foreground">
            Acquisti collettivi dell'associazione
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchOpportunities} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Aggiorna
          </Button>
          {user?.is_association_admin && (
            <Button onClick={() => window.location.href = '/association?tab=groupages'}>
              <Plus className="h-4 w-4 mr-2" />
              Nuovo Groupage
            </Button>
          )}
        </div>
      </div>

      {/* Banner ordini in attesa di chiusura groupage */}
      {myOrdersCount > 0 && !user?.is_association_admin && (
        <Card className="bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-800">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-100 dark:bg-emerald-900 rounded-lg">
                  <ShoppingCart className="h-5 w-5 text-emerald-600" />
                </div>
                <div>
                  <p className="font-medium text-emerald-800 dark:text-emerald-200">
                    Hai {myOrdersCount} {myOrdersCount === 1 ? 'ordine' : 'ordini'} in attesa
                  </p>
                  <p className="text-sm text-emerald-600 dark:text-emerald-400">
                    Verranno processati quando i groupage si chiuderanno. Puoi ancora modificarli!
                  </p>
                </div>
              </div>
              <Button 
                variant="outline" 
                className="border-emerald-300 text-emerald-700 hover:bg-emerald-100"
                onClick={() => setActiveTab('my-orders')}
              >
                I Miei Ordini
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Opportunità Attive</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{activeCount}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">I Miei Ordini</CardTitle>
            <ShoppingCart className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{myOrdersCount}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Risparmiato</CardTitle>
            <Euro className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">-</div>
            <p className="text-xs text-muted-foreground">rispetto ai prezzi singoli</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="active" className="gap-2">
            Attive
            {activeCount > 0 && (
              <Badge variant="secondary" className="ml-1">{activeCount}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="my-orders" className="gap-2">
            I Miei Ordini
            {myOrdersCount > 0 && (
              <Badge variant="secondary" className="ml-1">{myOrdersCount}</Badge>
            )}
          </TabsTrigger>
          {user?.is_association_admin && (
            <TabsTrigger value="drafts" className="gap-2">
              Bozze
              {draftsCount > 0 && (
                <Badge variant="secondary" className="ml-1">{draftsCount}</Badge>
              )}
            </TabsTrigger>
          )}
          <TabsTrigger value="past">Passate</TabsTrigger>
          {user?.is_association_admin && (
            <TabsTrigger value="deleted" className="gap-2 text-red-600">
              Eliminate
              {deletedCount > 0 && (
                <Badge variant="secondary" className="ml-1 bg-red-100 text-red-600">{deletedCount}</Badge>
              )}
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value={activeTab} className="mt-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-6 w-6 animate-spin" />
            </div>
          ) : filteredOpportunities.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Package className="h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-muted-foreground">
                  {activeTab === 'active' 
                    ? 'Nessuna opportunità attiva al momento'
                    : activeTab === 'my-orders'
                    ? 'Non hai ancora inserito ordini'
                    : activeTab === 'drafts'
                    ? 'Nessuna bozza da pubblicare'
                    : 'Nessuna opportunità passata'}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {filteredOpportunities.map((opportunity) => (
                <Card key={opportunity.id} className="flex flex-col">
                  <CardHeader>
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <CardTitle className="text-lg">{opportunity.title}</CardTitle>
                        <CardDescription className="flex items-center gap-1 mt-1">
                          <Building2 className="h-3 w-3" />
                          {opportunity.seller_company_name}
                        </CardDescription>
                      </div>
                      {getStatusBadge(opportunity.status, opportunity.collection_end)}
                    </div>
                  </CardHeader>
                  
                  <CardContent className="flex-1">
                    {opportunity.description && (
                      <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                        {opportunity.description}
                      </p>
                    )}
                    
                    <div className="space-y-3">
                      {/* Scadenza */}
                      <div className="flex items-center gap-2 text-sm">
                        <Calendar className="h-4 w-4 text-muted-foreground" />
                        <span>
                          {opportunity.collection_end && `Chiude il ${format(parseISO(opportunity.collection_end), 'd MMMM yyyy', { locale: it })}`}
                        </span>
                      </div>
                      
                      {/* Partecipanti */}
                      <div className="flex items-center gap-2 text-sm">
                        <Users className="h-4 w-4 text-muted-foreground" />
                        <span>
                          {opportunity.participants_count} partecipanti
                          {opportunity.min_participants > 1 && ` (min. ${opportunity.min_participants})`}
                        </span>
                      </div>
                      
                      {/* Prodotti */}
                      <div className="flex items-center gap-2 text-sm">
                        <Package className="h-4 w-4 text-muted-foreground" />
                        <span>{opportunity.products.length} prodotti disponibili</span>
                      </div>
                      
                      {/* Consegna */}
                      {opportunity.expected_delivery_start && (
                        <div className="flex items-center gap-2 text-sm">
                          <Truck className="h-4 w-4 text-muted-foreground" />
                          <span>
                            {opportunity.expected_delivery_start && `Consegna prevista: ${format(parseISO(opportunity.expected_delivery_start), 'd MMM', { locale: it })}`}
                            {opportunity.expected_delivery_start && opportunity.expected_delivery_end && ` - ${format(parseISO(opportunity.expected_delivery_end), 'd MMM', { locale: it })}`}
                          </span>
                        </div>
                      )}
                    </div>
                    
                    {/* Il mio ordine */}
                    {opportunity.my_order && (
                      <div className="mt-4 p-4 bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 rounded-lg">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <Check className="h-5 w-5 text-emerald-600" />
                            <span className="font-medium text-emerald-800 dark:text-emerald-200">Hai già ordinato!</span>
                          </div>
                          <Badge 
                            variant="outline" 
                            className={
                              opportunity.my_order.status === 'confirmed' 
                                ? 'bg-emerald-100 text-emerald-700 border-emerald-300' 
                                : 'bg-blue-100 text-blue-700 border-blue-300'
                            }
                          >
                            {opportunity.my_order.status === 'submitted' ? 'Inviato' : 
                             opportunity.my_order.status === 'confirmed' ? 'Confermato' : 
                             opportunity.my_order.status === 'draft' ? 'Bozza' :
                             opportunity.my_order.status === 'pending' ? 'In elaborazione' :
                             opportunity.my_order.status === 'processed' ? 'Processato' :
                             opportunity.my_order.status}
                          </Badge>
                        </div>
                        <div className="flex items-baseline justify-between">
                          <div>
                            <div className="text-2xl font-bold text-emerald-700 dark:text-emerald-300">
                              € {(opportunity.my_order?.total || 0).toFixed(2)}
                            </div>
                            <div className="text-sm text-emerald-600 dark:text-emerald-400">
                              {opportunity.my_order.items.length} {opportunity.my_order.items.length === 1 ? 'prodotto' : 'prodotti'}
                            </div>
                          </div>
                          {['published', 'collecting'].includes(opportunity.status) && (
                            <div className="text-xs text-emerald-600 dark:text-emerald-400 text-right">
                              <Clock className="h-3 w-3 inline mr-1" />
                              Puoi modificare fino alla chiusura
                            </div>
                          )}
                        </div>
                        {opportunity.my_order.items.length > 0 && (
                          <div className="mt-3 pt-3 border-t border-emerald-200 dark:border-emerald-700">
                            <div className="text-xs text-emerald-600 dark:text-emerald-400 space-y-1">
                              {opportunity.my_order.items.slice(0, 3).map((item: any, idx: number) => (
                                <div key={idx} className="flex justify-between">
                                  <span>{item.quantity}x {item.product_name || item.name}</span>
                                  <span>€ {((item.quantity || 0) * (item.unit_price || 0)).toFixed(2)}</span>
                                </div>
                              ))}
                              {opportunity.my_order.items.length > 3 && (
                                <div className="text-emerald-500">+ altri {opportunity.my_order.items.length - 3} prodotti</div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                  
                  <CardFooter className="pt-4 border-t">
                    {/* Bozze - pulsanti admin */}
                    {['draft', 'pending_approval'].includes(opportunity.status) && user?.is_association_admin ? (
                      <div className="flex gap-2 w-full">
                        <Button 
                          className="flex-1"
                          onClick={() => handlePublishGroupage(opportunity.id)}
                        >
                          <Send className="h-4 w-4 mr-2" />
                          Pubblica
                        </Button>
                        <Button 
                          variant="outline"
                          onClick={() => window.location.href = `/association?tab=groupages&edit=${opportunity.id}`}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="outline"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => openDeleteDialog(opportunity)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ) : ['published', 'collecting'].includes(opportunity.status) && user?.is_association_admin ? (
                      /* Groupage attivo - pulsanti admin */
                      <div className="flex flex-col gap-2 w-full">
                        <Button 
                          className="w-full" 
                          onClick={() => openOrderDialog(opportunity)}
                          data-testid={`order-btn-${opportunity.id}`}
                        >
                          {opportunity.my_order ? 'Modifica Ordine' : 'Ordina'}
                          <ChevronRight className="h-4 w-4 ml-1" />
                        </Button>
                        <div className="grid grid-cols-4 gap-2">
                          <Button 
                            variant="outline"
                            size="sm"
                            onClick={() => openAdminDetail(opportunity)}
                            className="col-span-1"
                          >
                            <BarChart3 className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="outline"
                            size="sm"
                            onClick={() => window.location.href = `/association?tab=groupages&edit=${opportunity.id}`}
                            className="col-span-1"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="outline"
                            size="sm"
                            onClick={() => handleCloseGroupage(opportunity.id)}
                            className="col-span-1"
                          >
                            <Clock className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="outline"
                            size="sm"
                            className="col-span-1 text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => openDeleteDialog(opportunity)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ) : opportunity.status === 'closed' && user?.is_association_admin ? (
                      /* Groupage chiuso - pulsante per completare */
                      <div className="flex flex-col gap-2 w-full">
                        <Button 
                          className="w-full"
                          onClick={() => handleCompleteGroupage(opportunity.id)}
                        >
                          <Send className="h-4 w-4 mr-2" />
                          Invia al Fornitore
                        </Button>
                        <div className="grid grid-cols-3 gap-2">
                          <Button 
                            variant="outline"
                            size="sm"
                            onClick={() => openAdminDetail(opportunity)}
                          >
                            <BarChart3 className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="outline"
                            size="sm"
                            onClick={() => window.location.href = `/association?tab=groupages&edit=${opportunity.id}`}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="outline"
                            size="sm"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => openDeleteDialog(opportunity)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ) : opportunity.status === 'completed' && user?.is_association_admin ? (
                      /* Groupage completato - solo visualizzazione */
                      <div className="flex gap-2 w-full">
                        <Button 
                          variant="outline"
                          className="flex-1"
                          onClick={() => openAdminDetail(opportunity)}
                        >
                          <BarChart3 className="h-4 w-4 mr-2" />
                          Riepilogo
                        </Button>
                      </div>
                    ) : canOrder(opportunity) ? (
                      <div className="flex gap-2 w-full">
                        <Button 
                          className="flex-1" 
                          onClick={() => openOrderDialog(opportunity)}
                          data-testid={`order-btn-${opportunity.id}`}
                        >
                          {opportunity.my_order ? 'Modifica Ordine' : 'Ordina'}
                          <ChevronRight className="h-4 w-4 ml-1" />
                        </Button>
                        {opportunity.my_order?.status === 'draft' && (
                          <Button
                            variant="outline"
                            onClick={() => handleConfirmOrder(opportunity.my_order!.id)}
                          >
                            <Check className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    ) : (
                      <Button variant="outline" className="w-full" disabled>
                        Raccolta chiusa
                      </Button>
                    )}
                  </CardFooter>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Order Dialog */}
      <Dialog open={showOrderDialog} onOpenChange={setShowOrderDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>{selectedOpportunity?.title}</DialogTitle>
            <DialogDescription>
              {selectedOpportunity?.seller_company_name} • 
              {selectedOpportunity?.collection_end && `Chiude il ${format(parseISO(selectedOpportunity.collection_end), 'd MMMM yyyy', { locale: it })}`}
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto py-4">
            {/* Prodotti */}
            <div className="space-y-3">
              <Label>Seleziona i prodotti</Label>
              
              {selectedOpportunity?.products.map((product) => {
                const qty = orderItems[product.product_id] || 0;
                const itemTotal = qty * product.unit_price;
                
                // Calcola disponibilità stock
                const hasStockLimit = selectedOpportunity?.stock_enabled && product.max_quantity_total;
                const stockRemaining = hasStockLimit 
                  ? (product.max_quantity_total! - (product.current_quantity_sold || 0))
                  : null;
                const isOutOfStock = stockRemaining !== null && stockRemaining <= 0;
                
                // Limite massimo effettivo (minimo tra max per buyer e stock rimanente)
                const effectiveMax = (() => {
                  let max = product.max_quantity_per_buyer || product.max_quantity;
                  if (stockRemaining !== null) {
                    max = max ? Math.min(max, stockRemaining) : stockRemaining;
                  }
                  return max;
                })();
                
                return (
                  <div
                    key={product.product_id}
                    className={`p-4 rounded-lg border ${
                      isOutOfStock 
                        ? 'border-red-300 bg-red-50 dark:bg-red-900/10 opacity-60' 
                        : qty > 0 
                          ? 'border-primary bg-primary/5' 
                          : 'border-border'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="font-medium flex items-center gap-2">
                          {product.product_name || product.name}
                          {isOutOfStock && (
                            <Badge variant="destructive" className="text-xs">Esaurito</Badge>
                          )}
                        </div>
                        {product.product_sku && (
                          <div className="text-xs text-muted-foreground">SKU: {product.product_sku}</div>
                        )}
                        <div className="text-sm text-primary font-medium mt-1">
                          € {(product.unit_price || 0).toFixed(2)} / {product.unit}
                        </div>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {product.min_quantity > 1 && (
                            <span className="text-xs text-muted-foreground">
                              Min. {product.min_quantity} {product.unit}
                            </span>
                          )}
                          {product.max_quantity_per_buyer && (
                            <span className="text-xs text-muted-foreground">
                              Max {product.max_quantity_per_buyer}/ordine
                            </span>
                          )}
                          {stockRemaining !== null && !isOutOfStock && (
                            <span className={`text-xs ${stockRemaining < 10 ? 'text-orange-600 font-medium' : 'text-muted-foreground'}`}>
                              Disponibili: {stockRemaining}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={() => updateQuantity(product.product_id, -1, product.min_quantity, effectiveMax)}
                          disabled={qty === 0 || isOutOfStock}
                        >
                          <Minus className="h-4 w-4" />
                        </Button>
                        
                        <Input
                          type="text"
                          inputMode="numeric"
                          value={qty}
                          onChange={(e) => {
                            const val = e.target.value.replace(/\D/g, '');
                            setQuantity(product.product_id, val, product.min_quantity, effectiveMax);
                          }}
                          className="w-20 text-center"
                          disabled={isOutOfStock}
                        />
                        
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={() => updateQuantity(product.product_id, 1, product.min_quantity, effectiveMax)}
                          disabled={isOutOfStock || (effectiveMax !== undefined && qty >= effectiveMax)}
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    
                    {qty > 0 && (
                      <div className="mt-2 pt-2 border-t flex justify-between items-center">
                        <span className="text-sm text-muted-foreground">Subtotale</span>
                        <span className="font-medium">€ {itemTotal.toFixed(2)}</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            
            <Separator className="my-6" />
            
            {/* Note */}
            <div className="space-y-4">
              <div>
                <Label htmlFor="delivery-notes">Note per la consegna</Label>
                <Textarea
                  id="delivery-notes"
                  value={deliveryNotes}
                  onChange={(e) => setDeliveryNotes(e.target.value)}
                  placeholder="Istruzioni speciali per la consegna..."
                  rows={2}
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label htmlFor="order-notes">Note per l'ordine</Label>
                <Textarea
                  id="order-notes"
                  value={orderNotes}
                  onChange={(e) => setOrderNotes(e.target.value)}
                  placeholder="Altre note..."
                  rows={2}
                  className="mt-1"
                />
              </div>
            </div>
            
            {/* Info pagamento e spedizione */}
            {selectedOpportunity?.payment_notes && (
              <div className="mt-4 p-3 bg-muted rounded-lg">
                <div className="text-sm font-medium mb-1">Modalità di pagamento</div>
                <div className="text-sm text-muted-foreground">{selectedOpportunity.payment_notes}</div>
              </div>
            )}
            
            {selectedOpportunity?.shipping_notes && (
              <div className="mt-2 p-3 bg-muted rounded-lg">
                <div className="text-sm font-medium mb-1">Informazioni spedizione</div>
                <div className="text-sm text-muted-foreground">{selectedOpportunity.shipping_notes}</div>
              </div>
            )}
          </div>
          
          {/* Riepilogo e azioni */}
          <div className="border-t pt-4">
            {(() => {
              const totals = calculateOrderTotal();
              const hasItems = Object.keys(orderItems).length > 0;
              
              return (
                <>
                  <div className="space-y-2 mb-4">
                    <div className="flex justify-between text-sm">
                      <span>Subtotale</span>
                      <span>€ {(totals.subtotal || 0).toFixed(2)}</span>
                    </div>
                    {totals.shipping > 0 && (
                      <div className="flex justify-between text-sm">
                        <span>Spedizione</span>
                        <span>€ {(totals.shipping || 0).toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between font-bold text-lg">
                      <span>Totale</span>
                      <span>€ {(totals.total || 0).toFixed(2)}</span>
                    </div>
                  </div>
                  
                  <DialogFooter className="gap-2">
                    {selectedOpportunity?.my_order?.status === 'draft' && (
                      <Button
                        variant="destructive"
                        onClick={() => {
                          handleCancelOrder(selectedOpportunity.my_order!.id);
                          setShowOrderDialog(false);
                        }}
                      >
                        Annulla Ordine
                      </Button>
                    )}
                    <Button variant="outline" onClick={() => setShowOrderDialog(false)}>
                      Chiudi
                    </Button>
                    <Button 
                      onClick={handleSubmitOrder} 
                      disabled={!hasItems || submitting}
                    >
                      {submitting ? (
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      ) : null}
                      {selectedOpportunity?.my_order ? 'Aggiorna Ordine' : 'Inserisci Ordine'}
                    </Button>
                  </DialogFooter>
                </>
              );
            })()}
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Dettaglio Admin */}
      <Dialog open={showAdminDetailDialog} onOpenChange={setShowAdminDetailDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              {adminDetailGroupage?.title}
            </DialogTitle>
            <DialogDescription>
              Andamento del groupage • {adminDetailGroupage?.seller_company_name}
            </DialogDescription>
          </DialogHeader>

          {loadingOrders ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto space-y-6">
              {/* Stats Cards */}
              <div className="grid grid-cols-4 gap-4">
                <Card className="bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200">
                  <CardContent className="pt-4">
                    <div className="text-2xl font-bold text-emerald-700">{groupageOrders.length}</div>
                    <div className="text-sm text-emerald-600">Ordini ricevuti</div>
                  </CardContent>
                </Card>
                <Card className="bg-blue-50 dark:bg-blue-950/30 border-blue-200">
                  <CardContent className="pt-4">
                    <div className="text-2xl font-bold text-blue-700">
                      € {groupageOrders.reduce((sum, o) => sum + (o.total || 0), 0).toFixed(0)}
                    </div>
                    <div className="text-sm text-blue-600">Totale ordinato</div>
                  </CardContent>
                </Card>
                <Card className="bg-amber-50 dark:bg-amber-950/30 border-amber-200">
                  <CardContent className="pt-4">
                    <div className="text-2xl font-bold text-amber-700">
                      {adminDetailGroupage?.min_participants || 0}
                    </div>
                    <div className="text-sm text-amber-600">Min. partecipanti</div>
                  </CardContent>
                </Card>
                <Card className="bg-purple-50 dark:bg-purple-950/30 border-purple-200">
                  <CardContent className="pt-4">
                    <div className="text-2xl font-bold text-purple-700">
                      {Math.round((groupageOrders.length / (adminDetailGroupage?.min_participants || 1)) * 100)}%
                    </div>
                    <div className="text-sm text-purple-600">Obiettivo</div>
                  </CardContent>
                </Card>
              </div>

              {/* Tabella ordini */}
              <div>
                <h3 className="text-lg font-medium mb-3 flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Ordini dei membri ({groupageOrders.length})
                </h3>
                
                {groupageOrders.length === 0 ? (
                  <Card>
                    <CardContent className="py-8 text-center text-muted-foreground">
                      <ShoppingCart className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p>Nessun ordine ancora</p>
                    </CardContent>
                  </Card>
                ) : (
                  <Card>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Membro</TableHead>
                          <TableHead>Data</TableHead>
                          <TableHead className="text-center">Prodotti</TableHead>
                          <TableHead className="text-right">Totale</TableHead>
                          <TableHead>Stato</TableHead>
                          <TableHead></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {groupageOrders.map((order) => (
                          <TableRow key={order.id}>
                            <TableCell>
                              <div className="flex flex-col">
                                <span className="font-medium">{order.company?.name || 'N/A'}</span>
                                <span className="text-xs text-muted-foreground">{order.user?.email}</span>
                              </div>
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {new Date(order.created_at).toLocaleDateString('it-IT')}
                            </TableCell>
                            <TableCell className="text-center">
                              {order.items?.length || 0}
                            </TableCell>
                            <TableCell className="text-right font-medium">
                              € {(order.total || 0).toFixed(2)}
                            </TableCell>
                            <TableCell>
                              <Badge variant={order.status === 'submitted' ? 'default' : 'secondary'}>
                                {order.status === 'submitted' ? 'Inviato' : 
                                 order.status === 'confirmed' ? 'Confermato' :
                                 order.status === 'draft' ? 'Bozza' :
                                 order.status === 'processed' ? 'Processato' :
                                 order.status}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => setSelectedBuyerOrder(selectedBuyerOrder?.id === order.id ? null : order)}
                              >
                                {selectedBuyerOrder?.id === order.id ? 'Chiudi' : 'Dettagli'}
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    
                    {/* Dettaglio ordine selezionato */}
                    {selectedBuyerOrder && (
                      <div className="p-4 bg-muted/50 border-t">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="font-medium">
                            Dettaglio ordine di {selectedBuyerOrder.company?.name}
                          </h4>
                          {selectedBuyerOrder.company?.phone && (
                            <span className="text-sm text-muted-foreground flex items-center gap-1">
                              <Phone className="h-3 w-3" />
                              {selectedBuyerOrder.company.phone}
                            </span>
                          )}
                        </div>
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Prodotto</TableHead>
                              <TableHead className="text-center">Quantità</TableHead>
                              <TableHead className="text-right">Prezzo unit.</TableHead>
                              <TableHead className="text-right">Totale</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {selectedBuyerOrder.items?.map((item: any, idx: number) => (
                              <TableRow key={idx}>
                                <TableCell>{item.product_name || item.name}</TableCell>
                                <TableCell className="text-center">{item.quantity} {item.unit}</TableCell>
                                <TableCell className="text-right">€ {(item.unit_price || 0).toFixed(2)}</TableCell>
                                <TableCell className="text-right font-medium">
                                  € {((item.quantity || 0) * (item.unit_price || 0)).toFixed(2)}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                        {selectedBuyerOrder.notes && (
                          <div className="mt-3 p-2 bg-background rounded text-sm">
                            <span className="font-medium">Note:</span> {selectedBuyerOrder.notes}
                          </div>
                        )}
                      </div>
                    )}
                  </Card>
                )}
              </div>

              {/* Riepilogo prodotti */}
              {groupageOrders.length > 0 && (
                <div>
                  <h3 className="text-lg font-medium mb-3 flex items-center gap-2">
                    <Package className="h-5 w-5" />
                    Riepilogo prodotti
                  </h3>
                  <Card>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Prodotto</TableHead>
                          <TableHead className="text-center">Ordini</TableHead>
                          <TableHead className="text-center">Qtà totale</TableHead>
                          <TableHead className="text-right">Valore totale</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(() => {
                          const productMap: Record<string, any> = {};
                          groupageOrders.forEach(order => {
                            order.items?.forEach((item: any) => {
                              if (!productMap[item.product_id]) {
                                productMap[item.product_id] = {
                                  name: item.product_name || item.name,
                                  unit: item.unit,
                                  orders: 0,
                                  quantity: 0,
                                  value: 0
                                };
                              }
                              productMap[item.product_id].orders++;
                              productMap[item.product_id].quantity += item.quantity;
                              productMap[item.product_id].value += item.quantity * item.unit_price;
                            });
                          });
                          return Object.entries(productMap)
                            .sort((a, b) => b[1].value - a[1].value)
                            .map(([id, p]) => (
                              <TableRow key={id}>
                                <TableCell className="font-medium">{p.name}</TableCell>
                                <TableCell className="text-center">{p.orders}</TableCell>
                                <TableCell className="text-center">{p.quantity} {p.unit}</TableCell>
                                <TableCell className="text-right font-medium">€ {(p.value || 0).toFixed(2)}</TableCell>
                              </TableRow>
                            ));
                        })()}
                      </TableBody>
                    </Table>
                  </Card>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="border-t pt-4">
            <Button variant="outline" onClick={() => setShowAdminDetailDialog(false)}>
              Chiudi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Dialog Conferma Eliminazione */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" />
              Elimina Groupage
            </AlertDialogTitle>
            <AlertDialogDescription>
              Sei sicuro di voler eliminare il groupage <strong>"{groupageToDelete?.title}"</strong>?
              {groupageToDelete?.status !== 'draft' && (
                <span className="block mt-2 text-amber-600">
                  ⚠️ Attenzione: questo groupage ha già degli ordini. Eliminandolo, tutti gli ordini associati verranno cancellati.
                </span>
              )}
              <span className="block mt-2">Questa azione non può essere annullata.</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteGroupage}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting ? 'Eliminazione...' : 'Elimina'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default GroupagePage;
